import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../api/axiosConfig';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Sms as SmsIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PageContainer from './common/PageContainer';
import PageHeader from './common/PageHeader';

const SECRET_KEYS = [
  'fast2sms_api_key',
  'razorpay_key_id',
  'razorpay_key_secret',
];

const PRESET_GROUPS = [
  {
    title: 'Billing Rules',
    icon: <CreditCardIcon color="primary" />,
    description: 'Configure grace period, message unit cost, commission, and minimum buyout.',
    keys: [
      { settingKey: 'subscription_grace_days', description: 'Subscription grace days after due date', settingType: 'INTEGRATION', settingValue: '5' },
      { settingKey: 'message_credit_unit_price', description: 'Message credit unit price', settingType: 'INTEGRATION', settingValue: '0.25' },
      { settingKey: 'message_credit_commission_percent', description: 'Message credit commission percent', settingType: 'INTEGRATION', settingValue: '10' },
      { settingKey: 'message_credit_min_purchase', description: 'Minimum message credit purchase', settingType: 'INTEGRATION', settingValue: '100' },
    ],
  },
  {
    title: 'Fast2SMS SMS',
    icon: <SmsIcon color="primary" />,
    description: 'Manage DLT/API SMS defaults for normal production messaging.',
    keys: [
      { settingKey: 'sms_vendor', description: 'Default SMS vendor', settingType: 'INTEGRATION', settingValue: 'fast2sms' },
      { settingKey: 'fast2sms_sms_route', description: 'Fast2SMS SMS route', settingType: 'INTEGRATION', settingValue: 'dlt_manual' },
      { settingKey: 'fast2sms_sender_id', description: 'Fast2SMS DLT sender id', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'fast2sms_entity_id', description: 'Fast2SMS DLT entity id', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'fast2sms_template_id', description: 'Fast2SMS DLT manual template id', settingType: 'INTEGRATION', settingValue: '' },
    ],
  },
  {
    title: 'Fast2SMS WhatsApp',
    icon: <SmsIcon color="primary" />,
    description: 'Control session/template mode and approved template configuration.',
    keys: [
      { settingKey: 'whatsapp_vendor', description: 'Default WhatsApp vendor', settingType: 'INTEGRATION', settingValue: 'fast2sms' },
      { settingKey: 'fast2sms_whatsapp_mode', description: 'Fast2SMS WhatsApp mode', settingType: 'INTEGRATION', settingValue: 'template' },
      { settingKey: 'fast2sms_whatsapp_phone_number_id', description: 'Fast2SMS WhatsApp phone number id', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'fast2sms_whatsapp_template_name', description: 'Fast2SMS WhatsApp template name', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'fast2sms_whatsapp_language_code', description: 'Fast2SMS WhatsApp template language code', settingType: 'INTEGRATION', settingValue: 'en' },
    ],
  },
  {
    title: 'Razorpay',
    icon: <KeyIcon color="primary" />,
    description: 'Set Razorpay pay-in keys and core checkout behavior.',
    keys: [
      { settingKey: 'razorpay_key_id', description: 'Razorpay key id', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'razorpay_key_secret', description: 'Razorpay key secret', settingType: 'INTEGRATION', settingValue: '' },
      { settingKey: 'razorpay_currency', description: 'Razorpay currency', settingType: 'INTEGRATION', settingValue: 'INR' },
      { settingKey: 'razorpay_auto_capture', description: 'Razorpay auto capture enabled', settingType: 'INTEGRATION', settingValue: 'true' },
    ],
  },
];

const maskSecret = (value) => {
  if (!value) return 'N/A';
  if (value.length <= 6) return '******';
  return `${value.slice(0, 3)}${'*'.repeat(Math.max(value.length - 6, 6))}${value.slice(-3)}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch (e) {
    return 'Invalid Date';
  }
};

const AdminSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentSetting, setCurrentSetting] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, setting: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/common-settings');
      setSettings(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to fetch settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const filteredSettings = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return settings.filter((item) => {
      const matchesType = typeFilter === 'ALL' || item.settingType === typeFilter;
      if (!normalizedQuery) return matchesType;
      const haystack = `${item.settingKey || ''} ${item.settingValue || ''} ${item.description || ''}`.toLowerCase();
      return matchesType && haystack.includes(normalizedQuery);
    });
  }, [searchQuery, settings, typeFilter]);

  const formik = useFormik({
    initialValues: {
      settingKey: '',
      settingValue: '',
      description: '',
      settingType: 'ADMIN',
    },
    validationSchema: Yup.object({
      settingKey: Yup.string().required('Setting Key is required').max(255, 'Maximum 255 characters'),
      settingValue: Yup.string(),
      description: Yup.string().max(500, 'Maximum 500 characters'),
      settingType: Yup.string().oneOf(['ADMIN', 'SCHOOL', 'INTEGRATION']).required('Setting Type is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = currentSetting
          ? { ...values, settingId: currentSetting.settingId }
          : values;

        await axiosInstance.post('/api/common-settings', payload);
        setSnackbar({
          open: true,
          message: currentSetting ? 'Setting updated successfully.' : 'Setting created successfully.',
          severity: 'success',
        });
        resetForm();
        setCurrentSetting(null);
        setOpenDialog(false);
        fetchSettings();
      } catch (err) {
        console.error('Error saving setting:', err);
        setSnackbar({ open: true, message: 'Failed to save setting.', severity: 'error' });
      }
    },
  });

  const handleAdd = () => {
    setCurrentSetting(null);
    formik.resetForm();
    setOpenDialog(true);
  };

  const openPreset = (preset) => {
    setCurrentSetting(null);
    formik.setValues({
      settingKey: preset.settingKey,
      settingValue: preset.settingValue ?? '',
      description: preset.description ?? '',
      settingType: preset.settingType ?? 'INTEGRATION',
    });
    setOpenDialog(true);
  };

  const presetCoverage = useMemo(() => {
    const keys = new Set(settings.map((item) => item.settingKey));
    return PRESET_GROUPS.map((group) => ({
      ...group,
      completed: group.keys.filter((item) => keys.has(item.settingKey)).length,
      total: group.keys.length,
    }));
  }, [settings]);

  const handleEdit = (setting) => {
    setCurrentSetting(setting);
    formik.setValues({
      settingKey: setting.settingKey || '',
      settingValue: setting.settingValue || '',
      description: setting.description || '',
      settingType: setting.settingType || 'ADMIN',
    });
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    const settingId = deleteConfirmation.setting?.settingId;
    if (!settingId) {
      setDeleteConfirmation({ open: false, setting: null });
      return;
    }

    try {
      await axiosInstance.delete(`/api/common-settings/${settingId}`);
      setSnackbar({ open: true, message: 'Setting deleted successfully.', severity: 'success' });
      fetchSettings();
    } catch (err) {
      console.error('Error deleting setting:', err);
      setSnackbar({ open: true, message: 'Failed to delete setting.', severity: 'error' });
    } finally {
      setDeleteConfirmation({ open: false, setting: null });
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setCurrentSetting(null);
    formik.resetForm();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Admin Settings"
        subtitle="Manage billing rules, Fast2SMS DLT configuration, Razorpay pay-in, and other platform-wide controls."
      />
      <Box sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsSuggestIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Admin Settings
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
              Centralize app-wide configuration and school-level defaults.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchSettings} color="primary" disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={loading}>
              Add Setting
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {presetCoverage.map((group) => (
          <Grid item xs={12} md={6} xl={3} key={group.title}>
            <Paper sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {group.icon}
                    <Typography sx={{ fontWeight: 700 }}>{group.title}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {group.completed}/{group.total}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {group.description}
                </Typography>
                <Stack spacing={0.75}>
                  {group.keys.slice(0, 3).map((preset) => (
                    <Button
                      key={preset.settingKey}
                      variant="outlined"
                      size="small"
                      onClick={() => openPreset(preset)}
                      sx={{ justifyContent: 'space-between' }}
                    >
                      {preset.settingKey}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by key, value, or description"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="setting-type-filter-label">Setting Type</InputLabel>
              <Select
                labelId="setting-type-filter-label"
                value={typeFilter}
                label="Setting Type"
                onChange={(e) => setTypeFilter(e.target.value)}
                startAdornment={<TuneIcon fontSize="small" sx={{ mr: 0.75, color: 'text.secondary' }} />}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ADMIN">ADMIN</MenuItem>
                <MenuItem value="SCHOOL">SCHOOL</MenuItem>
                <MenuItem value="INTEGRATION">INTEGRATION</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 0, backgroundColor: 'transparent' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '24%', fontWeight: 700 }}>Key</TableCell>
                <TableCell sx={{ width: '24%', fontWeight: 700 }}>Value</TableCell>
                <TableCell sx={{ width: '26%', fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ width: '10%', fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ width: '10%', fontWeight: 700 }}>Updated</TableCell>
                <TableCell align="center" sx={{ width: '6%', fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSettings.length > 0 ? (
                filteredSettings.map((setting) => (
                  <TableRow key={setting.settingId} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{setting.settingKey}</Typography>
                    </TableCell>
                    <TableCell>{SECRET_KEYS.includes(setting.settingKey) ? maskSecret(setting.settingValue || '') : (setting.settingValue || 'N/A')}</TableCell>
                    <TableCell>{setting.description || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                        {setting.settingType}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem' }}>
                      {formatDate(setting.updatedAt)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton color="primary" size="small" onClick={() => handleEdit(setting)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteConfirmation({ open: true, setting })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No settings matched your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentSetting ? 'Edit Setting' : 'Add Setting'}</DialogTitle>
        <Divider />
        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                id="settingKey"
                name="settingKey"
                label="Setting Key"
                value={formik.values.settingKey}
                onChange={formik.handleChange}
                error={formik.touched.settingKey && Boolean(formik.errors.settingKey)}
                helperText={formik.touched.settingKey && formik.errors.settingKey}
              />
              <TextField
                size="small"
                fullWidth
                id="settingValue"
                name="settingValue"
                label="Setting Value"
                value={formik.values.settingValue}
                onChange={formik.handleChange}
              />
              <TextField
                size="small"
                fullWidth
                id="description"
                name="description"
                label="Description"
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
              <FormControl fullWidth size="small">
                <InputLabel id="settingType-label">Setting Type</InputLabel>
                <Select
                  labelId="settingType-label"
                  id="settingType"
                  name="settingType"
                  label="Setting Type"
                  value={formik.values.settingType}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="ADMIN">ADMIN</MenuItem>
                  <MenuItem value="SCHOOL">SCHOOL</MenuItem>
                  <MenuItem value="INTEGRATION">INTEGRATION</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {currentSetting ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, setting: null })}
      >
        <DialogTitle>Delete Setting</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteConfirmation.setting?.settingKey}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, setting: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AdminSettings;
